import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { getFirmLogoMeta } from "@/lib/firm-logo-meta";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar";

/**
 * Unified firm logo renderer. Mirrors the landing site's approach to dark/light mode:
 * - Colored logos (blueberry, aquafunded, fintokei, …) render unchanged in both themes.
 * - Monochrome white-on-transparent logos (FTMO, Alpha Capital) are flagged
 *   `invertInLight` and get `brightness-0 dark:brightness-100` — black in light
 *   mode (so they're legible on a white card), original white in dark mode.
 *
 * Default rendering is a plain <img>. Pass `asAvatar` (or use <FirmAvatar/>) to
 * get a shadcn Avatar with circular framing + letter fallback when the image fails.
 *
 * NEVER apply `dark:invert` blindly — that corrupts anything with brand color.
 */
export function FirmLogo({
  src,
  name,
  className,
  title,
  invertInLight,
}: {
  src: string;
  name: string;
  className?: string;
  title?: string;
  /** Override the firm-name lookup; pass explicitly if the name doesn't match the registry. */
  invertInLight?: boolean;
}): ReactNode {
  const meta = getFirmLogoMeta(name);
  const shouldInvert = invertInLight ?? meta.invertInLight ?? false;

  return (
    <img
      src={src}
      alt={name}
      title={title ?? name}
      loading="lazy"
      className={cn(
        "object-contain",
        shouldInvert && "brightness-0 dark:brightness-100",
        className
      )}
    />
  );
}

/**
 * Firm logo rendered inside a shadcn Avatar — circular, with a two-letter
 * fallback when the source image fails to load. Use for card headers, avatar
 * groups, and any treatment where the logo needs a consistent circular frame.
 */
export function FirmAvatar({
  src,
  name,
  className,
  size = "default",
  title,
  invertInLight,
}: {
  src: string;
  name: string;
  className?: string;
  size?: "sm" | "default" | "lg";
  title?: string;
  invertInLight?: boolean;
}): ReactNode {
  const meta = getFirmLogoMeta(name);
  const shouldInvert = invertInLight ?? meta.invertInLight ?? false;
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("") || name[0]?.toUpperCase() || "?";

  return (
    <Avatar size={size} className={cn("bg-card", className)} title={title ?? name}>
      <AvatarImage
        src={src}
        alt={name}
        className={cn(
          "object-contain p-1",
          shouldInvert && "brightness-0 dark:brightness-100"
        )}
      />
      <AvatarFallback className="bg-accent/10 text-[10px] font-semibold text-accent">
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}
